import React from 'react';
import dayjs from 'dayjs';
import { Typography, Tag, List, Divider, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import './EventDetailsDisplay.scss'; // Import the SCSS file

const { Text, Link, Paragraph } = Typography;

// This component purely displays event details.
// It does not include action buttons like "Select" or "Clear",
// as those are context-dependent.
const EventDetailsDisplay = ({ eventResource, displayMode = 'full' /* 'full' or 'minimal' */ }) => {
  const { t } = useTranslation();
  
  if (!eventResource) {
    return null;
  }

  // Defensive checks for properties, especially nested ones like start.dateTime
  const startTime = eventResource.start?.dateTime || eventResource.start?.date || eventResource.start;
  const endTime = eventResource.end?.dateTime || eventResource.end?.date || eventResource.end;
  const description = eventResource.description || eventResource.descriptionSnippet; // Prefer full description if available
  const organizer = eventResource.organizer?.email || eventResource.organizerEmail;

  const getAttendeeTag = (attendee) => {
    if (attendee.resource) return <Tag className="attendee-tag" color="blue">{t('eventDetails.attendeeTags.meetingRoom')}</Tag>;
    if (attendee.email === organizer) return <Tag className="attendee-tag" color="purple">{t('eventDetails.attendeeTags.organizer')}</Tag>; 
    if (attendee.responseStatus === 'declined') return <Tag className="attendee-tag" color="red">{t('eventDetails.attendeeTags.declined')}</Tag>;
    if (attendee.responseStatus === 'tentative') return <Tag className="attendee-tag" color="orange">{t('eventDetails.attendeeTags.tentative')}</Tag>;
    if (attendee.responseStatus === 'accepted' || attendee.responseStatus === 'needsAction' || !attendee.responseStatus) return <Tag className="attendee-tag" color="green">{t('eventDetails.attendeeTags.participant')}</Tag>; // Default to participant
    return null;
  };

  return (
    <div className="event-details-display">
      <Text strong className="event-title">
        {eventResource.title || eventResource.summary || t('eventDetails.labels.noTitle')}
      </Text>
      <Divider className="custom-divider" />

      <Paragraph className="info-paragraph">
        <Text strong>{t('eventDetails.labels.time')}</Text>
        {startTime ? dayjs(startTime).format('YYYY/MM/DD HH:mm') : 'N/A'} -
        {endTime ? dayjs(endTime).format(eventResource.isAllDay || (eventResource.start && !eventResource.start.dateTime && eventResource.start.date) ? 'YYYY/MM/DD' : 'HH:mm') : 'N/A'}
        {eventResource.isAllDay && <Tag className="all-day-tag">{t('eventDetails.labels.allDay')}</Tag>}
      </Paragraph>

      {displayMode === 'full' && eventResource.location && (
        <>
          <Paragraph className="info-paragraph">
            <Text strong>{t('eventDetails.labels.location')}</Text>{eventResource.location}
          </Paragraph>
        </>
      )}

      {displayMode === 'full' && organizer && (
        <>
          <Paragraph className="info-paragraph">
            <Text strong>{t('eventDetails.labels.organizer')}</Text>{organizer}
          </Paragraph>
          <Divider className="custom-divider" />
        </>
      )}

      {description && (
        <>
          <div className="description-container">
            <Text strong className="description-label">{t('eventDetails.labels.description')}</Text>
            <Paragraph className="description-paragraph" ellipsis={{ rows: 4, expandable: true, symbol: t('eventDetails.actions.expand') }}>
              {description}
            </Paragraph>
          </div>
          <Divider className="custom-divider" />
        </>
      )}

      {eventResource.attendees && eventResource.attendees.length > 0 && (
        <>
          <div className="attendees-hover-container">
            <Tooltip
              title={
                <div style={{ padding: '4px 0', maxWidth: '320px' }}>
                  {eventResource.attendees.map((attendee, index) => (
                    <div 
                      key={index} 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 8px',
                        margin: '3px 0',
                        borderRadius: '4px',
                        backgroundColor: '#3a3a3a',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#4a4a4a'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#3a3a3a'}
                    >
                      <span style={{
                        marginRight: '8px',
                        wordBreak: 'break-word',
                        fontSize: '13px',
                        color: '#ffffff',
                        lineHeight: '1.4',
                        flex: 1
                      }}>
                        {attendee.displayName || attendee.email}
                      </span>
                      <div style={{ transform: 'scale(0.85)' }}>
                        {getAttendeeTag(attendee)}
                      </div>
                    </div>
                  ))}
                </div>
              }
              placement="top"
            >
              <Paragraph className="info-paragraph attendees-summary">
                <Text strong>{t('eventDetails.labels.attendees')} ({eventResource.attendeesCount || eventResource.attendees.length})</Text>
              </Paragraph>
            </Tooltip>
          </div>
        </>
      )}

      {eventResource.attachments && eventResource.attachments.length > 0 && displayMode === 'full' && (
        <>
          <div className="list-container attachments-list-container">
            <Text strong className="list-label">{t('eventDetails.labels.attachments')}</Text>
            <List
              size="small"
              className="attachments-list"
              dataSource={eventResource.attachments}
              renderItem={att => (
                <List.Item>
                  <Link href={att.fileUrl || att.url} target="_blank" title={att.title}>{att.title}</Link>
                </List.Item>
              )}
            />
          </div>
          {/* <Divider className="custom-divider" /> */}
        </>
      )}

      {displayMode === 'full' && (eventResource.hangoutLink || eventResource.meetUrl) && (
        <>
          <Paragraph className="info-paragraph">
            <Text strong>{t('eventDetails.labels.meetingLink')}</Text>
            <Link href={eventResource.hangoutLink || eventResource.meetUrl} target="_blank">
              {eventResource.hangoutLink?.includes('meet.google.com') || eventResource.meetUrl?.includes('meet.google.com') ? t('eventDetails.actions.openGoogleMeet') : t('eventDetails.actions.openMeetingLink')}
            </Link>
          </Paragraph>
        </>
      )}
      
      {displayMode === 'full' && eventResource.link && (
        <Paragraph className="info-paragraph">
          <Text strong>{t('eventDetails.labels.calendarLink')}</Text>
          <Link href={eventResource.link} target="_blank">{t('eventDetails.actions.viewInGoogleCalendar')}</Link>
        </Paragraph>
      )}
    </div>
  );
};

export default EventDetailsDisplay; 